import { useMemo } from 'react';
import { TripType, useGlobalContext } from '@honeycomb/data';
/**
 * Hook to calculate the last selectable month for a calendar based on trip type and config settings.
 *
 * The config settings are strings which can be either:
 *  - A full date string (e.g., "2026-08-31")
 *  - A numeric string representing months from today (e.g., "36")
 *
 * The result is always clamped to the last day of the month.
 * Invalid or missing config values return `null`.
 */
export const useGetCalendarEndMonth = (tripType) => {
    const { configSettings: { DepartUntilFlights, DepartUntilHotels, DepartUntilTransfers }, } = useGlobalContext();
    /**
     * Parses a config value string into a Date.
     * Returns null if the value is missing or invalid.
     */
    const parseEndDate = (value) => {
        if (!value)
            return null;
        // If the value is numeric, treat it as "today + N months"
        if (/^\d+$/.test(value)) {
            const months = parseInt(value);
            const d = new Date();
            d.setMonth(d.getMonth() + months);
            return d;
        }
        // Otherwise, treat it as a full date string
        const fullDate = new Date(value);
        return Number.isNaN(fullDate.getTime()) ? null : fullDate;
    };
    /**
     * Clamps any date to the last day of its month.
     */
    const endOfMonth = (date) => {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0);
    };
    // Memoize calculation for performance since config values are static
    return useMemo(() => {
        let endDate = null;
        switch (tripType) {
            case TripType.Flight:
                endDate = parseEndDate(DepartUntilFlights);
                break;
            case TripType.Hotel:
            case TripType.Holiday:
                // Both use the hotel config setting
                endDate = parseEndDate(DepartUntilHotels);
                break;
            case TripType.Transfer:
                endDate = parseEndDate(DepartUntilTransfers);
                break;
            default:
                return null;
        }
        // Clamp to end of month or return null if invalid
        return endDate ? endOfMonth(endDate) : null;
    }, [tripType, DepartUntilFlights, DepartUntilHotels, DepartUntilTransfers]);
};
